// Bölüm 1: İş ekipmanı var mı? Türler (A: firma+operatör, B: sadece periyodik) ve A grubu için ekipman firması
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Building2, CheckCircle2, HardHat, Plus, Wrench } from 'lucide-react';
import { CraneIcon } from './CraneIcon';
import { EquipmentKindImage } from './EquipmentKindImage';
import { BehalfFirmPicker } from './BehalfFirmPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useEquipmentKinds = () => {
  const [kinds, setKinds] = useState([]);
  useEffect(() => { axios.get(`${API}/equipment-subtypes`).then(r => setKinds(r.data)).catch(() => {}); }, []);
  return kinds;
};

export const equipmentAnswerDone = (formData, kinds) => {
  if (formData.has_equipment === false) return true;
  if (formData.has_equipment !== true || !(formData.equipment_kinds || []).length) return false;
  const needsFirm = kinds.some(k => k.main_type === 'arac' && formData.equipment_kinds.includes(k.subtype_key));
  return !needsFirm || !!formData.equipment_company_id;
};

const KindCard = ({ kind, index, on, toggle }) => (
  <button type="button" onClick={toggle} data-testid={`eq-kind-${kind.subtype_key}`} aria-pressed={on}
    className={`group flex items-center gap-3 rounded-xl border bg-white px-2.5 py-2 text-left shadow-sm transition-colors ${on ? 'border-blue-900 ring-2 ring-blue-900/20' : 'border-slate-200 hover:border-blue-300'}`}>
    <EquipmentKindImage kind={kind} index={index} />
    <span className={`flex-1 text-sm leading-tight ${on ? 'font-semibold text-blue-900' : 'font-medium text-slate-800'}`}>{kind.subtype_label}</span>
    {on ? <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-900" /> : <Plus className="h-5 w-5 shrink-0 text-slate-400 group-hover:text-blue-700" />}
  </button>
);

const Group = ({ icon: Icon, title, hint, items, selected, toggle, tone, cols, testId }) => {
  const count = items.filter(k => selected.includes(k.subtype_key)).length;
  return (
    <div className={`rounded-xl p-4 ${tone}`} data-testid={testId}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          <Icon className="mt-0.5 h-7 w-7 text-slate-700" />
          <div>
            <p className="text-base font-semibold text-slate-900">{title}</p>
            <p className="text-xs text-slate-500">{hint}</p>
          </div>
        </div>
        {count > 0 && <span className="shrink-0 rounded-full bg-blue-900 px-2.5 py-0.5 text-xs font-semibold text-white">{count} seçili</span>}
      </div>
      <div className={`grid gap-2 ${cols}`}>{items.map((k, i) => <KindCard key={k.id} kind={k} index={i} on={selected.includes(k.subtype_key)} toggle={() => toggle(k.subtype_key)} />)}</div>
    </div>
  );
};

export const EquipmentQuestion = ({ formData, set, kinds, guardRemoveKinds = (keys, apply) => apply() }) => {
  const selected = formData.equipment_kinds || [];
  const toggle = (key) => (selected.includes(key)
    ? guardRemoveKinds([key], () => set({ equipment_kinds: selected.filter(k => k !== key) }))
    : set({ equipment_kinds: [...selected, key] }));
  const groupA = kinds.filter(k => k.main_type === 'arac');
  const groupB = kinds.filter(k => k.main_type === 'is_ekipmani');
  const needsFirm = groupA.some(k => selected.includes(k.subtype_key));
  const answered = formData.has_equipment === true || formData.has_equipment === false;
  return (
    <div className={`!max-w-none overflow-hidden rounded-xl border-2 bg-white ${answered ? 'border-slate-200' : 'border-amber-300'}`} data-testid="eq-question" data-guide-required={answered ? undefined : 'true'}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-slate-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-900 text-white"><HardHat className="h-6 w-6" /></span>
          <div>
            <h4 className="text-lg font-semibold text-slate-900" data-testid="eq-title">İş Ekipmanı</h4>
            <p className="text-sm text-slate-500">Talepte iş ekipmanı var mı? (vinç, manlift, kompresör vb.)</p>
          </div>
        </div>
        <div className="flex gap-2">
          {[[true, 'Evet, var'], [false, 'Hayır, yok']].map(([v, l]) => (
            <button key={l} type="button" data-testid={`eq-answer-${v ? 'yes' : 'no'}`}
              onClick={() => (v ? set({ has_equipment: true }) : guardRemoveKinds(selected, () => set({ has_equipment: false, equipment_kinds: [], equipment_company_id: '', equipment_company_name: '' })))}
              className={`h-10 rounded-lg border px-5 text-sm font-semibold transition-colors ${formData.has_equipment === v ? 'border-blue-900 bg-blue-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}>{l}</button>
          ))}
        </div>
      </div>
      {!answered && <p className="px-4 py-2 text-xs font-medium text-amber-700" data-testid="eq-answer-required">Devam etmek için bu soruyu yanıtlayın.</p>}
      {formData.has_equipment === true && (
        <div className="space-y-3 rounded-xl border-2 border-transparent p-4" data-guide-required={selected.length ? undefined : 'true'}>
          <Group icon={CraneIcon} title="A Grubu – Araç / İş Makinesi" hint="Firması ve operatörüyle gelir; ekipman firmasının belgeleri ve personeli istenir. Periyodik Muayene + Mali Mesuliyet Sigortası."
            items={groupA} selected={selected} toggle={toggle} tone="bg-sky-50" cols="sm:grid-cols-2 xl:grid-cols-3" testId="eq-group-a" />
          <Group icon={Wrench} title="B Grubu – İş Ekipmanı" hint="Sadece Periyodik Muayene (Bakım Belgesi opsiyonel)."
            items={groupB} selected={selected} toggle={toggle} tone="bg-orange-50/70" cols="sm:grid-cols-2 xl:grid-cols-4" testId="eq-group-b" />
          {!selected.length && <p className="flex items-center gap-1.5 text-sm font-medium text-amber-700" data-testid="eq-kind-required"><AlertCircle className="h-4 w-4" />En az bir ekipman türü seçin.</p>}
          {needsFirm && (
            <div className="space-y-2 rounded-lg border-2 border-blue-200 bg-white p-3" data-testid="eq-company-section" data-guide-required={formData.equipment_company_id ? undefined : 'true'}>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Building2 className="h-4 w-4 text-blue-900" />Ekipmanı getirecek firma</p>
              <BehalfFirmPicker name={formData.equipment_company_name} behalfId={formData.equipment_company_id} suffix="" testPrefix="eq-company"
                placeholder="Ekipmanı getirecek firmanın adı"
                onPick={(f) => set({ equipment_company_name: f?.name || '', equipment_company_id: f?.id || '' })} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
